import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import PublicHeader from '@/components/PublicHeader';
import { Check, Sparkles, Building2, Users, Zap, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // tier id being checked out

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/tiers`);
      const data = await res.json();
      setTiers(data);
    } catch (err) {
      console.error('Failed to fetch pricing tiers:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'free_trial': return <Sparkles className="w-6 h-6" />;
      case 'starter': return <Users className="w-6 h-6" />;
      case 'medium': return <Building2 className="w-6 h-6" />;
      case 'large': return <Zap className="w-6 h-6" />;
      case 'enterprise': return <Building2 className="w-6 h-6" />;
      default: return <Users className="w-6 h-6" />;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case 'free_trial': return 'from-emerald-500 to-teal-500';
      case 'starter': return 'from-blue-500 to-cyan-500';
      case 'medium': return 'from-violet-500 to-purple-500';
      case 'large': return 'from-amber-500 to-orange-500';
      case 'enterprise': return 'from-slate-700 to-slate-900';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const formatPrice = (tier) => {
    if (tier.id === 'enterprise' || tier.price_per_user === null) return 'Custom';
    if (tier.price_per_user === 0) return 'Free';
    return `$${tier.price_per_user}`;
  };

  const getSubtitle = (tier) => {
    if (tier.id === 'enterprise' || tier.price_per_user === null) return 'tailored pricing';
    if (tier.price_per_user === 0) return `${tier.trial_days} day trial`;
    return 'per user/month';
  };

  const handleStartTrial = () => {
    navigate('/register');
  };

  const handleContactSales = () => {
    window.location.href = 'mailto:info@projectai.com?subject=Pulse Enterprise Inquiry';
  };

  const handleSubscribe = async (tierId) => {
    const token = localStorage.getItem('pulse_token');
    if (!token) {
      navigate('/login');
      return;
    }

    setCheckoutLoading(tierId);
    try {
      const res = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: tierId,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-teal-600 border-teal-200">
            Simple, Transparent Pricing
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Choose the plan that fits your team
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-12">
            Start with a free trial. Scale as you grow. No hidden fees.
            All plans include full access to AI-powered insights.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {tiers.map((tier, index) => (
              <Card 
                key={tier.id} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  tier.id === 'medium' ? 'ring-2 ring-violet-500 scale-105' : ''
                }`}
              >
                {tier.id === 'medium' && (
                  <div className="absolute top-0 right-0 bg-violet-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${getTierColor(tier.id)}`} />
                
                <CardHeader className="pb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getTierColor(tier.id)} text-white flex items-center justify-center mb-4`}>
                    {getTierIcon(tier.id)}
                  </div>
                  <CardTitle className="text-xl">{tier.display_name}</CardTitle>
                  <CardDescription>
                    {tier.id === 'enterprise'
                      ? 'Custom deployment'
                      : tier.min_users === 0 
                        ? `Up to ${tier.max_users} users`
                        : tier.max_users 
                          ? `${tier.min_users}-${tier.max_users} users`
                          : `${tier.min_users}+ users`
                    }
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pb-6">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">
                      {formatPrice(tier)}
                    </span>
                    <span className="text-slate-500 ml-1">
                      {getSubtitle(tier)}
                    </span>
                  </div>
                  
                  {tier.min_monthly_cost > 0 && tier.id !== 'enterprise' && (
                    <p className="text-sm text-slate-500 mb-4">
                      Minimum ${tier.min_monthly_cost}/month
                    </p>
                  )}
                  
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  <Button 
                    className={`w-full ${
                      tier.id === 'medium' 
                        ? 'bg-violet-600 hover:bg-violet-700' 
                        : tier.id === 'free_trial'
                          ? 'bg-teal-600 hover:bg-teal-700'
                          : tier.id === 'enterprise'
                            ? 'bg-slate-800 hover:bg-slate-900'
                            : ''
                    }`}
                    variant={tier.id === 'large' ? 'outline' : 'default'}
                    disabled={checkoutLoading === tier.id}
                    onClick={
                      tier.id === 'free_trial' ? handleStartTrial :
                      tier.id === 'large' || tier.id === 'enterprise' ? handleContactSales :
                      () => handleSubscribe(tier.id)
                    }
                    data-testid={`pricing-btn-${tier.id}`}
                  >
                    {checkoutLoading === tier.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
                    ) : (
                      <>{tier.id === 'free_trial' ? 'Start Free Trial' : 
                        tier.id === 'enterprise' ? 'Contact Us' :
                        tier.id === 'large' ? 'Contact Sales' : 'Subscribe Now'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What happens if I exceed my user limit?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We use a soft limit approach with a 10% overage cap. If you exceed your paid user count by up to 10%, 
                  the extra users will be billed at your plan's rate on the following month's invoice. 
                  If you exceed 10%, you'll need to upgrade your plan or remove users.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I change plans at any time?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Yes! You can upgrade your plan at any time. When you upgrade, you'll be prorated for the remainder 
                  of your billing cycle. Downgrades take effect at the start of your next billing period.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What's included in the free trial?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  The free trial includes full access to all features for 30 days, with support for up to 100 
                  invited users and 5 projects. No credit card required to start.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do you count users?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We count "invited users" - stakeholders who have been invited to participate in surveys 
                  via email. Reference-only stakeholders (added for network mapping but not invited) don't 
                  count toward your user limit.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to understand your stakeholders?</h2>
          <p className="text-xl text-slate-600 mb-8">
            Start your free trial today. No credit card required.
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg" onClick={handleStartTrial} className="bg-teal-600 hover:bg-teal-700">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={handleContactSales}>
              Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-teal-600">pulse</span>
              <span className="text-xs text-slate-400">by projectai</span>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} ProjectAI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
